'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * The dock's contextual slots (docs/MOBILE.md §2.4): the floating dock
 * reserves its right end for the ONE primary action of the current surface,
 * and (since 2026-07-18) its nav region for a page-claimed OVERRIDE row -
 * the item page's Back / "…" cluster replaces the four nav homes there.
 *
 * A page claims a slot by mounting `<DockAction>` / `<DockNav>` with the
 * control as children (registration, not a portal target ref: entries carry a
 * stable id, so route transitions where the next page registers before the
 * previous one unregisters resolve cleanly - last claimant wins). No claimant
 * = the shell renders its default (the create action; the nav links).
 * `<DockAction>{null}</DockAction>` claims the slot and renders nothing - for
 * a surface that wants no default create in the thumb zone.
 */

interface Entry {
  id: string;
  node: ReactNode;
}

export interface DockActionRegistry {
  register: (id: string, node: ReactNode) => void;
  unregister: (id: string) => void;
}

const DockActionContext = createContext<DockActionRegistry | null>(null);
const DockNavContext = createContext<DockActionRegistry | null>(null);

export const DockActionProvider = DockActionContext.Provider;
export const DockNavProvider = DockNavContext.Provider;

/** Shell-side state for one slot: a claim stack, the latest claimant wins.
 *  Called once per slot (action, nav override) in the app shell. */
export function useDockActionSlot(): {
  registry: DockActionRegistry;
  /** true when any page claimed the slot (even with an empty node). */
  claimed: boolean;
  action: ReactNode;
} {
  const [entries, setEntries] = useState<Entry[]>([]);
  const register = useCallback((id: string, node: ReactNode) => {
    setEntries((prev) => {
      // A re-register keeps its position: replacing in place (not re-append)
      // stops an earlier claimant's re-render from stealing the slot back.
      const i = prev.findIndex((e) => e.id === id);
      if (i === -1) return [...prev, { id, node }];
      const next = prev.slice();
      next[i] = { id, node };
      return next;
    });
  }, []);
  const unregister = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);
  const registry = useMemo(() => ({ register, unregister }), [register, unregister]);
  const last = entries[entries.length - 1];
  return { registry, claimed: entries.length > 0, action: last?.node ?? null };
}

/** Register `children` into a slot registry for this component's lifetime. */
function useSlotClaim(registry: DockActionRegistry | null, children: ReactNode) {
  const id = useId();
  // Register on every children change; unregister only on unmount (a paired
  // cleanup would flash the shell default between re-registrations).
  useEffect(() => {
    registry?.register(id, children);
  }, [registry, id, children]);
  useEffect(() => {
    if (!registry) return;
    return () => registry.unregister(id);
  }, [registry, id]);
}

/** Claim the dock's page-action region (the accent act at the right end). */
export function DockAction({ children }: { children: ReactNode }) {
  useSlotClaim(useContext(DockActionContext), children);
  return null;
}

/** Claim the dock's NAV region: the children render in place of the shell's
 *  nav links (the item page's contextual Back / "…" row). */
export function DockNav({ children }: { children: ReactNode }) {
  useSlotClaim(useContext(DockNavContext), children);
  return null;
}
